import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowUp, ArrowDown, Minus, Gauge, Maximize2, Flame } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, ReferenceLine, YAxis } from "recharts";
import { fetchInflation } from "@/lib/api";

const TARGET = 2.0;

function changeStyle(change) {
  if (change == null) return { Icon: Minus, cls: "text-zinc-500" };
  if (change > 0) return { Icon: ArrowUp, cls: "text-neg" };
  if (change < 0) return { Icon: ArrowDown, cls: "text-pos" };
  return { Icon: Minus, cls: "text-zinc-500" };
}

function levelCls(value) {
  if (value == null) return "text-zinc-500";
  if (value <= TARGET + 0.001) return "text-pos";
  if (value <= TARGET + 1.0) return "text-amber-term";
  return "text-neg";
}

function Metric({ label, metric }) {
  if (!metric) {
    return (
      <div className="flex items-center justify-between" data-testid={`metric-${label.toLowerCase()}-na`}>
        <span className="text-[11px] uppercase tracking-widest text-zinc-600">{label}</span>
        <span className="text-xs text-zinc-700 tabular-nums">n/a</span>
      </div>
    );
  }
  const { Icon, cls } = changeStyle(metric.change);
  const chg = metric.change == null ? "" : `${metric.change > 0 ? "+" : ""}${metric.change.toFixed(1)}`;
  return (
    <div className="flex items-center justify-between" data-testid={`metric-${label.toLowerCase()}`}>
      <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
      <span className="inline-flex items-baseline gap-1.5">
        <span className={`text-sm font-semibold tabular-nums ${levelCls(metric.value)}`}>{metric.value.toFixed(1)}%</span>
        <span className={`inline-flex items-center text-[11px] tabular-nums ${cls}`}>
          <Icon size={10} strokeWidth={2.5} />{chg}
        </span>
      </span>
    </div>
  );
}

function PipelineFlag({ pipeline, showText }) {
  if (!pipeline) return null;
  const high = pipeline.level === "high";
  const cls = high ? "text-neg" : "text-amber-term";
  const title = `Pipeline pressure (${pipeline.level}): PPI ${pipeline.ppi}% vs CPI ${pipeline.cpi}% (+${pipeline.gap}pp). Producer costs are running above consumer prices — may push CPI higher.`;
  return (
    <span className={`inline-flex items-center gap-0.5 ${cls}`} title={title} data-testid="pipeline-flag">
      <Flame size={11} strokeWidth={2} className={high ? "animate-pulse" : ""} />
      {showText && <span className="text-[10px] font-semibold uppercase tracking-widest tabular-nums">PPI +{pipeline.gap}</span>}
    </span>
  );
}

function EconomyCard({ e, compact }) {
  const headline = e.headline;
  const spark = (headline?.trend || []).slice(-6).map((p) => ({ ...p }));
  const aboveTarget = headline && headline.value > TARGET + 0.001;
  const hChg = changeStyle(headline?.change);

  if (compact) {
    return (
      <Link
        to="/inflation"
        className="group w-full text-left flex items-center gap-2 py-2 px-2 border-b border-zinc-900 last:border-0 hover:bg-zinc-900/50 focus:outline-none focus:bg-zinc-900/60 transition-colors"
        data-testid={`inflation-card-${e.code.toLowerCase()}`}
      >
        <span className="text-[11px] font-semibold text-amber-term uppercase w-8">{e.code}</span>
        <span className="text-[11px] text-zinc-500 flex-1 truncate">{e.name}</span>
        <span className="inline-flex items-center gap-0.5">
          <hChg.Icon size={10} strokeWidth={2.5} className={hChg.cls} />
          <span className={`text-sm font-semibold tabular-nums ${levelCls(headline?.value)}`}>
            {headline ? `${headline.value.toFixed(1)}%` : "n/a"}
          </span>
        </span>
        <span className="text-[11px] tabular-nums text-zinc-500 w-16 text-right">
          core {e.core ? `${e.core.value.toFixed(1)}%` : "—"}
        </span>
        <PipelineFlag pipeline={e.pipeline} />
        <Maximize2 size={10} strokeWidth={1.5} className="text-zinc-700 group-hover:text-amber-term transition-colors" />
      </Link>
    );
  }

  return (
    <Link
      to="/inflation"
      className="group text-left border border-zinc-800 bg-[#09090B] p-3 flex flex-col gap-2 min-w-0 transition-colors hover:border-amber-500/70 focus:outline-none focus:border-amber-500"
      data-testid={`inflation-card-${e.code.toLowerCase()}`}
    >
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-semibold text-amber-term uppercase tracking-wider">{e.code} · {e.name}</span>
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-zinc-600">
          <PipelineFlag pipeline={e.pipeline} showText />
          {e.central_bank}
          <Maximize2 size={10} strokeWidth={1.5} className="text-zinc-700 group-hover:text-amber-term transition-colors" />
        </span>
      </div>
      <Metric label="HEADLINE" metric={e.headline} />
      <Metric label="CORE" metric={e.core} />
      <Metric label="PPI" metric={e.ppi} />
      <div className="h-[34px] -mx-1">
        {spark.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={spark} margin={{ top: 6, right: 4, left: 4, bottom: 2 }}>
              <YAxis hide domain={["dataMin - 0.5", "dataMax + 0.5"]} />
              <ReferenceLine y={TARGET} stroke="#3F3F46" strokeDasharray="2 2" />
              <Line type="monotone" dataKey="value" stroke={aboveTarget ? "#EF4444" : "#10B981"} strokeWidth={1.5} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full" />
        )}
      </div>
      {e.review && (
        <p className="text-[11px] leading-snug text-zinc-400 border-t border-zinc-900 pt-1.5" data-testid={`inflation-review-${e.code.toLowerCase()}`}>
          {e.review}
        </p>
      )}
      <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-zinc-600">
        <span>{headline?.date || "—"}</span>
        <span className="inline-flex items-center gap-1"><Gauge size={10} strokeWidth={1.5} /> TGT {TARGET.toFixed(1)}%</span>
      </div>
    </Link>
  );
}

export default function InflationStrip({ variant }) {
  const q = useQuery({ queryKey: ["inflation"], queryFn: fetchInflation, staleTime: 1000 * 60 * 60 });
  const economies = q.data?.economies || [];

  if (variant === "compact") {
    return (
      <div className="border border-zinc-800 bg-[#09090B] h-full" data-testid="inflation-strip">
        <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-widest text-zinc-400">▍ Inflation · CPI YoY</span>
          <Link to="/inflation" className="text-[11px] uppercase tracking-widest text-zinc-600 hover:text-amber-term transition-colors inline-flex items-center gap-1" data-testid="inflation-open-page">
            Monitor <Maximize2 size={10} strokeWidth={1.5} />
          </Link>
        </div>
        <div className="px-1">
          {economies.length
            ? economies.map((e) => <EconomyCard key={e.code} e={e} compact />)
            : Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-[38px] m-1 bg-zinc-900/60 animate-pulse" data-testid={`inflation-skeleton-${i}`} />
              ))}
        </div>
        <div className="px-3 py-1.5 text-[11px] uppercase tracking-widest text-zinc-600 border-t border-zinc-900">
          {q.data?.sources || (q.isLoading ? "loading…" : "")} · click for full monitor
        </div>
      </div>
    );
  }

  return (
    <div data-testid="inflation-strip">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500">INFLATION · CPI YoY vs 2.0% TARGET</span>
        <Link to="/inflation" className="text-[11px] uppercase tracking-widest text-zinc-700 ml-auto hover:text-amber-term transition-colors" data-testid="inflation-open-page">
          Open Monitor ↗
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {economies.length
          ? economies.map((e) => <EconomyCard key={e.code} e={e} />)
          : Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="border border-zinc-800 bg-[#09090B] p-3 h-[180px] animate-pulse" data-testid={`inflation-skeleton-${i}`} />
            ))}
      </div>
    </div>
  );
}
